// Settings → Payment Terms tab.
//
// Source-of-truth render lives in portal-standalone.html:
//   • Tab button:           ~2754   (settings-tab[data-tab="payment-terms"])
//   • Inline fallback UI:   31744-31838 (kosInlinePaymentTermsUI / kosWirePaymentTermsUI)
//   • Panel renderer:       32122-32151 (renderPaymentTermsPanel)
//   • localStorage key:     `kos_payment_schedule_v1`  (line 31756)
//
// Spec — TKOS_MASTER_CONTEXT.md §SETTINGS / Payment Terms; inventory
// 01_retailer.md §6 confirms three-default-stage seed (Deposit 50% → On order;
// Pre-delivery 40% → Before delivery; Final 10% → On completion), running
// total live-validation, "+ Add stage", "Reset to default", "Save schedule".
//
// Backend wiring
// --------------
// kitchenos-api currently has NO `/api/portal/settings/payment-terms`
// endpoint (grepped: no hits in modules/*.js or server.js). The portal-
// standalone implementation persisted only to localStorage. We therefore:
//   1. POST to `/api/portal/settings/payment-terms` and tolerate 404/501 by
//      saving only to localStorage and surfacing a "saved locally" toast.
//   2. Always mirror to localStorage `tkos.settings.payment_terms` on save —
//      that key is the immediate UI source-of-truth and is read by the
//      Quote Builder when seeding new quotes.
// When the backend lands, the tab will start round-tripping automatically.

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab, TabCleanup } from "../types";
import type { PageContext } from "../../../shell/types";

import "./payment-terms.css";

// ── Types ────────────────────────────────────────────────────────────────────

type TriggerEvent =
  | "on_order"
  | "before_delivery"
  | "on_completion"
  | "custom_date";

interface Stage {
  id: string;
  label: string;
  pct: number;
  trigger: TriggerEvent;
}

interface PaymentTermsConfig {
  stages: Stage[];
}

const TRIGGERS: { value: TriggerEvent; label: string }[] = [
  { value: "on_order",         label: "On order" },
  { value: "before_delivery",  label: "Before delivery" },
  { value: "on_completion",    label: "On completion" },
  { value: "custom_date",      label: "Custom date" },
];

const LS_KEY = "tkos.settings.payment_terms";

// Default seed from the master spec.
const DEFAULT_STAGES: readonly Stage[] = Object.freeze([
  { id: "ps-deposit",  label: "Deposit",       pct: 50, trigger: "on_order" },
  { id: "ps-pre-del",  label: "Pre-delivery",  pct: 40, trigger: "before_delivery" },
  { id: "ps-final",    label: "Final payment", pct: 10, trigger: "on_completion" },
]);

// ── Persistence ──────────────────────────────────────────────────────────────

function loadLocal(): PaymentTermsConfig | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PaymentTermsConfig> | null;
    if (!parsed || !Array.isArray(parsed.stages)) return null;
    const stages = parsed.stages
      .filter((s): s is Stage => !!s && typeof s.label === "string")
      .map<Stage>((s) => ({
        id: typeof s.id === "string" && s.id ? s.id : `ps-${Math.random().toString(36).slice(2, 9)}`,
        label: String(s.label).slice(0, 80),
        pct: clampPct(Number(s.pct)),
        trigger: TRIGGERS.some((t) => t.value === s.trigger) ? s.trigger : "on_order",
      }));
    return { stages };
  } catch {
    return null;
  }
}

function saveLocal(cfg: PaymentTermsConfig): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(cfg));
  } catch {
    /* localStorage may be full / disabled — non-fatal. */
  }
}

function clampPct(n: number): number {
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 100) return 100;
  return Math.round(n * 10) / 10;
}

function freshId(): string {
  return `ps-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function defaultConfig(): PaymentTermsConfig {
  return { stages: DEFAULT_STAGES.map((s) => ({ ...s })) };
}

function sumPct(stages: Stage[]): number {
  return stages.reduce((acc, s) => acc + (Number.isFinite(s.pct) ? s.pct : 0), 0);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Backend (best-effort; stub-tolerant) ─────────────────────────────────────

async function fetchRemote(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<PaymentTermsConfig | null> {
  try {
    const res = await ctx.api<{ stages?: Stage[] }>(
      "/portal/settings/payment-terms",
      { signal },
    );
    if (!res || !Array.isArray(res.stages)) return null;
    return { stages: res.stages };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      return null;
    }
    throw err;
  }
}

async function saveRemote(
  ctx: PageContext,
  cfg: PaymentTermsConfig,
  signal: AbortSignal,
): Promise<{ ok: boolean; remote: boolean }> {
  try {
    await ctx.api<{ ok?: boolean }>("/portal/settings/payment-terms", {
      method: "POST",
      body: JSON.stringify(cfg),
      signal,
    });
    return { ok: true, remote: true };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      // Backend not yet wired — that's expected until kitchenos-api ships
      // the route. Treat as "saved locally" so the user gets feedback.
      return { ok: true, remote: false };
    }
    throw err;
  }
}

// ── Render ───────────────────────────────────────────────────────────────────

function triggerOptions(selected: TriggerEvent): string {
  return TRIGGERS.map(
    (t) =>
      `<option value="${t.value}"${t.value === selected ? " selected" : ""}>${escapeHtml(t.label)}</option>`,
  ).join("");
}

function rowHtml(s: Stage, index: number, total: number): string {
  return `
    <li class="set-pt-row" data-id="${escapeHtml(s.id)}" draggable="false">
      <span class="set-pt-handle" aria-hidden="true" title="Drag handle (use Up/Down buttons to reorder)">⋮⋮</span>
      <span class="set-pt-num" aria-hidden="true">${index + 1}</span>
      <input class="set-pt-input set-pt-label" type="text" data-f="label"
             placeholder="Stage name"
             value="${escapeHtml(s.label)}"
             aria-label="Stage ${index + 1} name" />
      <div class="set-pt-pct-wrap">
        <input class="set-pt-input set-pt-pct" type="number" data-f="pct"
               min="0" max="100" step="0.5"
               value="${s.pct}"
               aria-label="Stage ${index + 1} percentage" />
        <span class="set-pt-pct-suffix">%</span>
      </div>
      <select class="set-pt-input set-pt-trigger" data-f="trigger"
              aria-label="Stage ${index + 1} trigger">${triggerOptions(s.trigger)}</select>
      <div class="set-pt-row-actions">
        <button type="button" class="set-pt-iconbtn" data-act="up"
                ${index === 0 ? "disabled" : ""} aria-label="Move up">▲</button>
        <button type="button" class="set-pt-iconbtn" data-act="down"
                ${index === total - 1 ? "disabled" : ""} aria-label="Move down">▼</button>
        <button type="button" class="set-pt-iconbtn set-pt-iconbtn-danger"
                data-act="remove" aria-label="Remove stage">×</button>
      </div>
    </li>
  `;
}

const tab: SettingsTab = {
  id: "payment-terms",
  label: "Payment Terms",
  icon: iconMarkup("pound", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    let state: PaymentTermsConfig = loadLocal() ?? defaultConfig();
    const controller = new AbortController();
    let disposed = false;

    container.innerHTML = `
      <section class="set-pt-root" aria-labelledby="set-pt-title">
        <header class="set-pt-header">
          <h2 id="set-pt-title" class="set-section-title">Default Payment Schedule</h2>
          <p class="set-help">Auto-applied to every new quote. Customisable per customer.</p>
        </header>

        <ol class="set-pt-list" data-role="list" role="list"></ol>

        <div class="set-pt-total" data-role="total"></div>

        <div class="set-pt-actions">
          <button type="button" class="set-btn set-btn-secondary" data-role="add">+ Add stage</button>
          <button type="button" class="set-btn set-btn-secondary" data-role="reset">Reset to default</button>
          <button type="button" class="set-btn" data-role="save">Save schedule</button>
        </div>
      </section>
    `;

    const listEl = container.querySelector<HTMLElement>("[data-role='list']")!;
    const totalEl = container.querySelector<HTMLElement>("[data-role='total']")!;
    const addBtn = container.querySelector<HTMLButtonElement>("[data-role='add']")!;
    const resetBtn = container.querySelector<HTMLButtonElement>("[data-role='reset']")!;
    const saveBtn = container.querySelector<HTMLButtonElement>("[data-role='save']")!;

    const renderList = (): void => {
      listEl.innerHTML = state.stages
        .map((s, i) => rowHtml(s, i, state.stages.length))
        .join("");
      renderTotal();
    };

    const renderTotal = (): void => {
      const total = sumPct(state.stages);
      const rounded = Math.round(total * 10) / 10;
      const isHundred = Math.abs(total - 100) < 0.05;
      const isOver = total > 100.05;
      totalEl.classList.toggle("set-pt-total--ok", isHundred);
      totalEl.classList.toggle("set-pt-total--bad", !isHundred);
      totalEl.classList.toggle("set-pt-total--over", isOver);
      // The .set-pt-total--ok / --bad / --over class drives the colour so
      // we don't need a glyph alongside the text — keeping it plain reads
      // cleaner in the professional dark theme.
      const status = isHundred
        ? "All stages add up to 100%"
        : isOver
        ? `Over by ${(rounded - 100).toFixed(1)}%`
        : `Under — ${(100 - rounded).toFixed(1)}% unallocated`;
      totalEl.textContent = `Total: ${rounded.toFixed(1)}%   ${status}`;
    };

    const setStages = (next: Stage[]): void => {
      state = { stages: next };
      renderList();
      // Persist UI immediately so a tab-switch doesn't lose edits.
      saveLocal(state);
    };

    const onListInput = (e: Event): void => {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      const row = t.closest<HTMLElement>(".set-pt-row");
      if (!row) return;
      const id = row.getAttribute("data-id");
      if (!id) return;
      const field = t.getAttribute("data-f");
      const stage = state.stages.find((s) => s.id === id);
      if (!stage) return;

      if (field === "label" && t instanceof HTMLInputElement) {
        stage.label = t.value;
      } else if (field === "pct" && t instanceof HTMLInputElement) {
        stage.pct = clampPct(parseFloat(t.value));
      } else if (field === "trigger" && t instanceof HTMLSelectElement) {
        const v = t.value as TriggerEvent;
        if (TRIGGERS.some((tt) => tt.value === v)) stage.trigger = v;
      }
      saveLocal(state);
      renderTotal();
    };

    const onListClick = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button[data-act]");
      if (!btn) return;
      const row = btn.closest<HTMLElement>(".set-pt-row");
      if (!row) return;
      const id = row.getAttribute("data-id");
      if (!id) return;
      const idx = state.stages.findIndex((s) => s.id === id);
      if (idx < 0) return;
      const act = btn.getAttribute("data-act");

      if (act === "remove") {
        if (state.stages.length <= 1) {
          ctx.showToast("At least one stage is required", "error");
          return;
        }
        setStages(state.stages.filter((_, i) => i !== idx));
      } else if (act === "up" && idx > 0) {
        const next = state.stages.slice();
        const [m] = next.splice(idx, 1);
        next.splice(idx - 1, 0, m!);
        setStages(next);
      } else if (act === "down" && idx < state.stages.length - 1) {
        const next = state.stages.slice();
        const [m] = next.splice(idx, 1);
        next.splice(idx + 1, 0, m!);
        setStages(next);
      }
    };

    const onAdd = (): void => {
      const remaining = Math.max(0, 100 - sumPct(state.stages));
      const newStage: Stage = {
        id: freshId(),
        label: "New stage",
        pct: clampPct(remaining),
        trigger: "on_order",
      };
      setStages([...state.stages, newStage]);
      // Focus the label of the newly-added row.
      const last = listEl.querySelector<HTMLInputElement>(
        `.set-pt-row[data-id='${newStage.id}'] input.set-pt-label`,
      );
      last?.focus();
      last?.select();
    };

    const onReset = (): void => {
      if (!confirm("Reset payment schedule to the default 50/40/10 split?")) return;
      setStages(defaultConfig().stages);
      ctx.showToast("Schedule reset to default", "info");
    };

    const onSave = async (): Promise<void> => {
      const total = sumPct(state.stages);
      if (Math.abs(total - 100) >= 0.05) {
        ctx.showToast(
          `Stages must add up to 100% (currently ${(Math.round(total * 10) / 10).toFixed(1)}%)`,
          "error",
        );
        return;
      }
      saveBtn.disabled = true;
      const originalLabel = saveBtn.textContent;
      saveBtn.textContent = "Saving…";
      try {
        const { remote } = await saveRemote(ctx, state, controller.signal);
        saveLocal(state);
        ctx.showToast(
          remote ? "Payment schedule saved" : "Saved locally (server route not yet wired)",
          "success",
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't save (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't save schedule";
        ctx.showToast(msg, "error");
      } finally {
        if (!disposed) {
          saveBtn.disabled = false;
          saveBtn.textContent = originalLabel;
        }
      }
    };

    listEl.addEventListener("input", onListInput);
    listEl.addEventListener("change", onListInput);
    listEl.addEventListener("click", onListClick);
    addBtn.addEventListener("click", onAdd);
    resetBtn.addEventListener("click", onReset);
    saveBtn.addEventListener("click", () => {
      void onSave();
    });

    // Initial render with whatever localStorage gave us; then merge any
    // server-side config that comes back (server wins).
    renderList();
    void (async () => {
      try {
        const remote = await fetchRemote(ctx, controller.signal);
        if (disposed || controller.signal.aborted) return;
        if (remote && remote.stages.length > 0) {
          state = remote;
          saveLocal(state);
          renderList();
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        // Non-404 fetch error — log only; UI already has localStorage data.
        // eslint-disable-next-line no-console
        console.warn("[settings/payment-terms] remote fetch failed:", err);
      }
    })();

    return (): void => {
      disposed = true;
      controller.abort();
      listEl.removeEventListener("input", onListInput);
      listEl.removeEventListener("change", onListInput);
      listEl.removeEventListener("click", onListClick);
    };
  },
};

export default tab;
